import { collectFilesRecursivelyFromItems } from "@components/common/sphere-dropzone/collect-files";
import { DropHandler } from "@components/common/sphere-dropzone/drop-handler";
import { DropzoneElements } from "@components/common/sphere-dropzone/dropzone-elements";
import {
  FileUploadTaskContext,
  UploadedFileResponse,
  UploadFileParams,
  UploadMultipleFilesParams,
} from "@custom-types/file-upload-types";
import { ReactSetStateFunction } from "@custom-types/types";
import { assert } from "@faro-lotv/foundation";
import { blue, neutral, useToast } from "@flat-ui";
import { isMaliciousSvgFile, validateFile } from "@hooks/file-upload-utils";
import {
  haveFileNamesValidLength,
  MAX_FILE_NAME_LENGTH,
} from "@hooks/upload-tasks/upload-tasks-utils";
import { useFileUpload } from "@hooks/use-file-upload";
import { Box, Typography } from "@mui/material";
import { FILE_SIZE_MULTIPLIER } from "@utils/file-utils";
import {
  compressImageFile,
  MAX_THUMBNAIL_UPLOAD_WIDTH,
} from "@utils/image-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { isNumber } from "es-toolkit";
import { ChangeEvent, DragEvent, useCallback, useState } from "react";

interface Props {
  /** Avatar component of user. If provided existingImageUrl will be ignored */
  avatar?: JSX.Element;

  /** Existing url of an image to display inside the dropzone */
  existingImageUrl?: string;

  /** True if uploading is in progress */
  isLoading: boolean;

  /** Callback function to trigger to set the loading state */
  setIsLoading: ReactSetStateFunction<boolean>;

  /** Callback function on file upload complete */
  onUploadComplete(
    uploadedResponse: UploadedFileResponse,
    context: FileUploadTaskContext
  ): void;

  /** Callback function which triggers on delete button click */
  onDeleteButtonClick?(): void;

  /** Text to show in delete icon tooltip */
  deleteIconTooltipText?: string;

  /** Give a title for the drag and drop zone */
  titleDragAndDrop?: string;

  /** Give the button upload file */
  hasUploadFileButton?: boolean;

  /** Whether the supported format should show in the dropzone */
  shouldShowSupportedFormats?: boolean;

  /** Whether the size limit should show in the dropzone */
  shouldShowSizeLimit?: boolean;

  /** Whether the dropzone should handle multiple file or not */
  shouldAllowMultiUpload?: boolean;

  /** Whether the dropzone should handle folders or not */
  shouldAllowFolderUpload?: boolean;

  /** Whether the dropzone should handle files */
  shouldAllowFiles?: boolean;

  /** List of file extensions that are allowed in the dropzone, e.g. ["gls"] */
  allowedExtensions?: string[];

  /** First part of instruction, can be different based on the usage of the dropzone */
  instruction?: string;

  /** Maximum file size the dropzone should allow to upload */
  maxFileSize?: number;

  /**
   * Additional information of the file upload task; required for most cases.
   */
  context: FileUploadTaskContext;

  /**
   * Callback function when new files are selected.
   * `confirmCallback` starts the upload, but the parent component can skip the call and use its own implementation.
   */
  onSelectFiles?(files: FileList | File[], confirmCallback: () => void): void;

  /**
   * Whether to compress the image before upload (for thumbnails, etc.)
   */
  shouldCompressImage?: boolean;
}

/** Contains all the sorted default file extensions that the default dropzone support  */
const DEFAULT_FILE_EXTENSIONS_WHITE_LIST = ["gif", "jpeg", "jpg", "png", "svg"];

/** Default maximum MB size of the file to upload */
const DEFAULT_MAX_UPLOAD_SIZE_MB: number = 10;

/** Maximum MB size of the file to use normal upload, higher than that will use chunk upload */
const MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_MB: number = 5;

/** Maximum byte size of the file to use normal upload, higher than that will use chunk upload */
const MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_BYTES =
  MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_MB *
  FILE_SIZE_MULTIPLIER *
  FILE_SIZE_MULTIPLIER;

/**
 * Renders the dropzone area to upload a file
 */
export function SphereDropzone({
  avatar,
  existingImageUrl,
  isLoading,
  setIsLoading,
  onUploadComplete,
  onDeleteButtonClick,
  deleteIconTooltipText,
  titleDragAndDrop,
  hasUploadFileButton,
  shouldShowSupportedFormats,
  shouldShowSizeLimit,
  allowedExtensions = DEFAULT_FILE_EXTENSIONS_WHITE_LIST,
  instruction,
  maxFileSize = DEFAULT_MAX_UPLOAD_SIZE_MB,
  shouldAllowMultiUpload = false,
  shouldAllowFolderUpload = false,
  shouldAllowFiles = true,
  context,
  onSelectFiles,
  shouldCompressImage = false,
}: Props): JSX.Element {
  const {
    uploadSingleFile,
    uploadFileWithChunks,
    uploadMultipleFiles,
    validateAndAddFailedTask,
  } = useFileUpload();
  const { openToast } = useToast();

  const [fileInputEl, setFileInputEl] = useState<HTMLInputElement | null>(null);
  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const openFileExplorer = useCallback(() => {
    if (!fileInputEl) {
      return;
    }
    // Allow to select folders only when openFileExplorer
    if (shouldAllowFiles === false) {
      // needed to to this cause react does not allow to set these attributes in the DOM/HTML
      // ... is not assignable to type 'DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
      fileInputEl.setAttribute("webkitdirectory", "true");
      fileInputEl.setAttribute("directory", "true");
    }
    // https://stackoverflow.com/a/76926836/4555850
    // We need oncancel to make this work: canBeDropped={!isFileExplorerOpen}.
    // eslint-disable-next-line react-compiler/react-compiler -- DOM element mutation in callback, not during render
    fileInputEl.oncancel = () => setIsFileExplorerOpen(false);
    fileInputEl.click();
    setIsFileExplorerOpen(true);
  }, [fileInputEl, shouldAllowFiles]);

  /** Selects files from file explorer after click on the dropzone */
  function selectFileFromDialog(event: ChangeEvent<HTMLInputElement>): void {
    setIsFileExplorerOpen(false);
    // When a folder was selected, we get the individual files instead of a FileSystemDirectoryHandle/Entry.
    // But we could still recover the folder structure using files[*].webkitRelativePath.
    const files = event.target.files;
    if (files) {
      const areValidFilesNames = haveFileNamesValidLength(files);
      if (!areValidFilesNames) {
        openToast({
          title: `Folder or file name is too large, please change it to be shorter than ${
            MAX_FILE_NAME_LENGTH + 1
          } characters`,
          variant: "warning",
        });
        return;
      }
    }
    handleFileSelection(files);
  }

  /** Selects files from drag and drop */
  async function selectFileFromDrop(
    event: DragEvent<HTMLElement>
  ): Promise<void> {
    event.preventDefault();
    if (event.dataTransfer) {
      /* Validate files/folders names, if too long it can lead to error or unexpected behavior */
      const areValidFilesNames = haveFileNamesValidLength(
        event.dataTransfer.files
      );
      if (!areValidFilesNames) {
        openToast({
          title: `Folder or file name is too large, please change it to be shorter than ${
            MAX_FILE_NAME_LENGTH + 1
          } characters`,
          variant: "warning",
        });
        return;
      }
      if (event.dataTransfer.items?.length >= 0) {
        const files = await collectFilesRecursivelyFromItems(
          event.dataTransfer.items,
          shouldAllowFolderUpload,
          shouldAllowFiles,
          allowedExtensions
        );
        handleFileSelection(files);
      } else {
        handleFileSelection(event.dataTransfer.files);
      }
    }
  }

  /** Handle files after selection or drop */
  function handleFileSelection(files: FileList | File[] | null): void {
    // Early return if there is no file dropped
    if (!files || files.length === 0) {
      return;
    }
    const allowedFiles: File[] = [];
    for (const file of files) {
      const validatedFile = validateFile({
        file,
        allowedExtensions,
        maxFileSize,
      });
      if (validatedFile.isValid) {
        allowedFiles.push(file);
      }
    }

    // Waiting for confirmation to start uploading if onSelectFiles callback is provided.
    if (onSelectFiles) {
      onSelectFiles(allowedFiles, () => {
        if (shouldAllowMultiUpload) {
          void initiateMultipleFileUpload(allowedFiles);
        } else {
          void initiateFileUpload(allowedFiles[0]);
        }
      });

      return;
    }

    // Proceed uploading if there is no confirmation required
    shouldAllowMultiUpload
      ? void initiateMultipleFileUpload(files)
      : void initiateFileUpload(files[0]);
  }

  /** Triggers when file upload starts */
  function onUploadStart(): void {
    // Progress loader should not visible to make the dropzone available for further upload
    if (!shouldAllowMultiUpload) {
      setIsLoading(true);
    }
  }

  /** Triggers to set the file upload progress value */
  function onUploadProgress(progress: ProgressEvent | number): void {
    const uploadProgress = isNumber(progress)
      ? progress
      : (progress.loaded / progress.total) * 100;

    setUploadProgress(uploadProgress);
  }

  /** Triggers when file is selected or dropped */
  async function initiateFileUpload(file: File | undefined): Promise<void> {
    // Early return if file is not available
    if (!file) {
      return;
    }

    const hasMaliciousCode = await isMaliciousSvgFile(file);

    if (hasMaliciousCode) {
      openToast({
        title: "Invalid SVG file",
        message: "This SVG contains potentially unsafe content.",
        variant: "error",
      });
      return;
    }

    const validatedFile = validateFile({
      file,
      allowedExtensions,
      maxFileSize,
    });
    if (!validatedFile.isValid) {
      openToast({
        title: validatedFile.message ?? "Invalid file",
        message: validatedFile.description,
        variant: "error",
      });

      return;
    }

    // Need to reset the progress to start from 0 next time
    setUploadProgress(0);

    assert(
      context,
      "SphereDropzone: Upload context is undefined, but required in this case"
    );

    // Compress image before upload if shouldCompressImage is true and file is an image
    let fileToUpload = file;
    if (shouldCompressImage && file.type.startsWith("image/")) {
      try {
        fileToUpload = await compressImageFile(
          file,
          MAX_THUMBNAIL_UPLOAD_WIDTH
        );
      } catch (error) {
        // If compression fails, fallback to uploading the original file
        sentryCaptureError({ error, title: "Failed to compress image" });
        fileToUpload = file;
      }
    }

    const uploadParams: UploadFileParams = {
      file: fileToUpload,
      onUploadStart,
      onUploadProgress,
      onUploadComplete,
      context,
    };

    // CoreAPI complains if we request a chunked upload without having a project ID.
    const isLargeFile = file.size > MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_BYTES;
    const shouldUseChunkUpload = !!context.projectId && isLargeFile;
    if (!shouldUseChunkUpload && isLargeFile) {
      // eslint-disable-next-line no-console -- Should be visible in development.
      console.warn(
        "Forcing non-chunked upload for large file because project ID was not provided."
      );
    }

    // Use chunk upload if file size is bigger than MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_MB.
    const isUploaded = shouldUseChunkUpload
      ? await uploadFileWithChunks(uploadParams)
      : await uploadSingleFile(uploadParams);

    // Hiding the loader spinner if upload is failed
    if (!isUploaded) {
      setIsLoading(false);
    }
  }

  async function initiateMultipleFileUpload(
    files: FileList | File[]
  ): Promise<void> {
    // Early return if projectId is not provided
    // Only for type checking
    assert(
      context,
      "SphereDropzone: Upload context is undefined, but required in this case"
    );
    if (!context.projectId) {
      return;
    }

    const uploadableFiles = [...files].filter((file) =>
      validateAndAddFailedTask({
        file,
        allowedExtensions,
        maxFileSize,
        context,
      })
    );

    // Return if there is no uploadable file
    if (!uploadableFiles.length) {
      return;
    }

    const uploadParams: UploadMultipleFilesParams = {
      files: uploadableFiles,
      onUploadStart,
      onUploadProgress,
      onUploadComplete,
      context,
    };

    await uploadMultipleFiles(uploadParams);
  }

  return (
    <Box component="div" data-testid="dropzone-container">
      {(titleDragAndDrop || hasUploadFileButton) && (
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {titleDragAndDrop && (
            <Typography
              sx={{
                fontWeight: 600,
                color: neutral[800],
                mb: "10px",
                fontSize: "14px",
              }}
            >
              {titleDragAndDrop}
            </Typography>
          )}

          {hasUploadFileButton && (
            <Typography
              sx={{
                fontWeight: 600,
                color: blue[500],
                mb: "10px",
                fontSize: "12px",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={openFileExplorer}
            >
              Upload file
            </Typography>
          )}
        </Box>
      )}
      <DropHandler
        onDrop={selectFileFromDrop}
        canBeDropped={!isFileExplorerOpen}
        onClick={openFileExplorer}
        isLoading={isLoading}
        shouldAllowMultiUpload={shouldAllowMultiUpload}
      >
        <DropzoneElements
          avatar={avatar}
          isLoading={isLoading}
          uploadProgress={uploadProgress}
          existingImageUrl={existingImageUrl}
          setFileInputEl={setFileInputEl}
          selectFileFromDialog={selectFileFromDialog}
          onDeleteButtonClick={onDeleteButtonClick}
          deleteIconTooltipText={deleteIconTooltipText}
          shouldShowSupportedFormats={shouldShowSupportedFormats}
          shouldShowSizeLimit={shouldShowSizeLimit}
          allowedExtensions={allowedExtensions}
          instruction={instruction}
          maxFileSize={maxFileSize}
          shouldAllowMultiUpload={shouldAllowMultiUpload}
          shouldAllowFiles={shouldAllowFiles}
        />
      </DropHandler>
    </Box>
  );
}
